
import { useEffect } from 'react';
import { useSEO } from '@/hooks/useSEO';

interface SEOHeadProps {
  pagePath: string;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

const SEOHead = ({ pagePath, fallbackTitle, fallbackDescription }: SEOHeadProps) => {
  const { seoData, loading } = useSEO(pagePath);

  useEffect(() => {
    if (loading) return;

    // Aplicar título
    const title = seoData?.title || fallbackTitle || 'TECNOSYNC';
    document.title = title;

    // Limpiar meta tags existentes
    const existingMetas = document.querySelectorAll('meta[data-seo]');
    existingMetas.forEach(meta => meta.remove());

    // Función para crear/actualizar meta tags
    const setMetaTag = (name: string, content: string, property?: string) => {
      if (!content) return;
      
      const meta = document.createElement('meta');
      meta.setAttribute('data-seo', 'true');
      
      if (property) {
        meta.setAttribute('property', property);
      } else {
        meta.setAttribute('name', name);
      }
      
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    };

    // Aplicar meta tags básicos
    if (seoData?.description || fallbackDescription) {
      setMetaTag('description', seoData?.description || fallbackDescription || '');
    }

    if (seoData?.keywords) {
      setMetaTag('keywords', seoData.keywords);
    }

    if (seoData?.author) {
      setMetaTag('author', seoData.author);
    }

    if (seoData?.copyright) {
      setMetaTag('copyright', seoData.copyright);
    }

    // Aplicar Open Graph tags
    if (seoData?.og_title || title) {
      setMetaTag('', seoData?.og_title || title, 'og:title');
    }

    if (seoData?.og_description || seoData?.description || fallbackDescription) {
      setMetaTag('', seoData?.og_description || seoData?.description || fallbackDescription || '', 'og:description');
    }

    if (seoData?.og_type) {
      setMetaTag('', seoData.og_type, 'og:type');
    }

    if (seoData?.og_url) {
      setMetaTag('', seoData.og_url, 'og:url');
    }

    if (seoData?.og_image) {
      setMetaTag('', seoData.og_image, 'og:image');
    }

    if (seoData?.og_image_alt) {
      setMetaTag('', seoData.og_image_alt, 'og:image:alt');
    }

    if (seoData?.og_site_name) {
      setMetaTag('', seoData.og_site_name, 'og:site_name');
    }

    // Aplicar canonical URL
    if (seoData?.canonical_url) {
      let existingCanonical = document.querySelector('link[rel="canonical"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }
      
      const canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', seoData.canonical_url);
      canonical.setAttribute('data-seo', 'true');
      document.head.appendChild(canonical);
    }

  }, [seoData, loading, fallbackTitle, fallbackDescription]);

  return null; // Este componente no renderiza nada visible
};

export default SEOHead;
